import React from "react";
import styled, { keyframes } from "styled-components";

/**
 * 긴 결과글 없이 '분포'만 보여주는 결과 화면 (resultMode: "chart").
 * 묶음 두 개를 한 장의 다각형 지도로 그립니다.
 *   - 첫 묶음은 위쪽 절반, 둘째 묶음은 아래쪽 절반
 *   - 값은 묶음 안에서의 비율(%)
 * 아래에는 묶음별 1위와 한 줄 설명을 붙입니다.
 */
const SIZE = 320;
const C = SIZE / 2;
const R = 112;
const ORANGE = "#F5931F";
const INK = "#1f2022";

const pctOf = (scores, traits) => {
  const total = traits.reduce((s, t) => s + (scores?.[t] || 0), 0);
  return Object.fromEntries(
    traits.map((t) => [t, total ? Math.round(((scores?.[t] || 0) / total) * 100) : 0])
  );
};

export const RadarMap = ({ title, groups, scores, tops, summaries }) => {
  if (!groups?.length) return null;
  const axes = groups.flatMap((g, gi) =>
    g.traits.map((t) => ({ trait: t, group: gi, pct: pctOf(scores, g.traits)[t] }))
  );
  const n = axes.length;
  // 첫 묶음이 위쪽 절반을 차지하도록 시작 각도를 맞춤
  const step = 360 / n;
  const start = -180 + step / 2;
  const maxPct = Math.max(1, ...axes.map((a) => a.pct));
  const pt = (i, r) => {
    const a = ((start + i * step) * Math.PI) / 180;
    return [C + r * Math.cos(a), C + r * Math.sin(a)];
  };
  const radius = (pct) => R * (0.12 + 0.88 * (pct / maxPct));
  const poly = axes.map((a, i) => pt(i, radius(a.pct)).join(",")).join(" ");
  const ring = (k) => axes.map((_, i) => pt(i, R * k).join(",")).join(" ");

  return (
    <Wrap aria-label={`${title} 지도`}>
      <Title>{title}</Title>
      <Svg viewBox={`0 0 ${SIZE} ${SIZE}`} role="img">
        {[0.34, 0.67, 1].map((k) => (
          <polygon key={k} points={ring(k)} fill="none" stroke="#e6e6e6" strokeWidth="1" />
        ))}
        {axes.map((_, i) => {
          const [x, y] = pt(i, R);
          return <line key={i} x1={C} y1={C} x2={x} y2={y} stroke="#ececec" strokeWidth="1" />;
        })}
        <Shape style={{ transformOrigin: `${C}px ${C}px` }}>
          <polygon points={poly} fill="rgba(245,147,31,0.18)" stroke={ORANGE} strokeWidth="2" strokeLinejoin="round" />
          {axes.map((a, i) => {
            const [x, y] = pt(i, radius(a.pct));
            const on = tops?.[a.group] === a.trait;
            return <circle key={a.trait} cx={x} cy={y} r={on ? 5 : 3} fill={on ? ORANGE : "#fff"} stroke={ORANGE} strokeWidth="1.5" />;
          })}
        </Shape>
        {axes.map((a, i) => {
          const [x, y] = pt(i, R + 26);
          const on = tops?.[a.group] === a.trait;
          return (
            <Label key={a.trait} style={{ animationDelay: `${0.5 + i * 0.06}s` }}>
              <text x={x} y={y - 3} textAnchor="middle" fontSize="12.5" fontWeight={on ? 700 : 400}
                fill={on ? INK : a.group === 0 ? "#555" : "#8a8a8a"}>
                {a.trait}
              </text>
              <text x={x} y={y + 12} textAnchor="middle" fontSize="11" fill={on ? ORANGE : "#aaa"}>
                {a.pct}%
              </text>
            </Label>
          );
        })}
      </Svg>
      <Legend>
        {groups.map((g, gi) => (
          <span key={g.name}>
            {gi === 0 ? "▲ 위쪽" : "▼ 아래쪽"} · {g.title}
          </span>
        ))}
      </Legend>

      {summaries?.map((s, i) =>
        s ? (
          <Summary key={s.label} style={{ animationDelay: `${0.9 + i * 0.15}s` }}>
            <small>{s.label}</small>
            <strong>
              {s.type} <em>{s.pct}%</em>
            </strong>
            {s.subtitle && <p>{s.subtitle}</p>}
          </Summary>
        ) : null
      )}
    </Wrap>
  );
};

export const groupPct = pctOf;

const grow = keyframes`
  from { transform: scale(0) rotate(-20deg); opacity: 0; }
  to   { transform: scale(1) rotate(0); opacity: 1; }
`;
const fade = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
`;

const Wrap = styled.section`
  width: 100%;
  margin-top: 24px;
  padding: 20px 16px 8px;
  border: 1px solid #e6e6e6;
  border-radius: 16px;
`;
const Title = styled.div`
  font-size: 15px;
  color: #555;
`;
const Svg = styled.svg`
  display: block;
  width: 100%;
  max-width: 340px;
  margin: 4px auto 0;
  overflow: visible;
`;
const Shape = styled.g`
  animation: ${grow} 0.9s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Label = styled.g`
  animation: ${fade} 0.4s ease both;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Legend = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 2px 0 10px;
  font-size: 12px;
  color: #999;
`;
const Summary = styled.div`
  padding: 14px 4px;
  border-top: 1px dashed #e2e2e2;
  animation: ${fade} 0.5s ease both;
  small { display: block; font-size: 12.5px; color: #999; }
  strong { display: block; margin-top: 4px; font-size: 18px; font-weight: 700; color: ${INK}; }
  em { font-style: normal; font-size: 14px; color: ${ORANGE}; margin-left: 4px; }
  p { margin: 6px 0 0; font-size: 14px; line-height: 1.6; color: #555; }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
